<script lang="ts">
  import { onMount } from 'svelte';
  import { surveyStore } from '$lib/stores/surveyStore';
  import { browser } from '$app/environment';
  import QuestionNavigator from './QuestionNavigator/QuestionNavigator.svelte';
  import ProfileNavigator from './ProfileNavigator/ProfileNavigator.svelte';
  import PreviewBarsToggle from './PreviewBarsToggle.svelte';
  import type { InterpretedSurvey } from '$lib/utils/surveyInterpreter';
  import { page } from '$app/stores';
  import { defaultTheme, getFontWithFallback } from '@walku/core';

  export let survey: InterpretedSurvey | undefined = undefined;

  let previewMode = false;
  let currentIndex = 0;
  let barsVisible = false;
  // Initialize isLocalhost immediately, not in onMount
  let isLocalhost = browser && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Extract theme from survey
  $: theme = survey?.theme || defaultTheme;
  $: finalFontFamily = theme?.fontFamily
    ? `"${theme.fontFamily}", sans-serif`
    : defaultTheme.fontFamily;

  // Check if preview UI is disabled via config
  $: disablePreviewUI = survey?.config?.disablePreviewUI ?? false;

  // Check if we're on a stats route, auth route, or the field-collection route
  $: isStatsRoute = $page.url.pathname.includes('/stats');
  $: isAuthRoute = $page.url.pathname.includes('/auth');
  $: isFieldRoute = $page.url.pathname.includes('/field');

  // Show preview components when preview mode OR localhost (not on stats/auth/field routes), unless disabled
  $: showPreviewComponents = !disablePreviewUI && (previewMode || isLocalhost) && !isStatsRoute && !isAuthRoute && !isFieldRoute;

  // Show toggle when preview components are visible
  $: showToggle = showPreviewComponents;

  // Subscribe to surveyStore to get preview mode and current index
  onMount(() => {
    const unsubscribe = surveyStore.subscribe(state => {
      previewMode = !!state?.previewMode;
      // Get the current index if available
      if (state?.currentQuestionIndex !== undefined) {
        currentIndex = state.currentQuestionIndex;
      }
    });

    return unsubscribe;
  });

  function handleToggle() {
    barsVisible = !barsVisible;
  }
</script>

<div class="shared-layout" 
  data-theme={theme.theme}
  style="
    --theme-primary-color: {theme.primaryColor};
    --theme-secondary-color: {theme.secondaryColor};
    --theme-success-color: {theme.successColor};
    --theme-danger-color: {theme.dangerColor};
    --theme-warning-color: {theme.warningColor};
    --theme-text-color: {theme.theme === 'dark' ? theme.textColorDark : theme.textColor};
    --theme-text-color-light: {theme.textColorLight};
    --theme-text-color-dark: {theme.textColorDark};
    --theme-text-on-primary: {theme.textOnPrimary};
    --theme-text-on-secondary: {theme.textOnSecondary};
    --theme-text-on-success: {theme.textOnSuccess};
    --theme-text-on-warning: {theme.textOnWarning};
    --theme-text-on-danger: {theme.textOnDanger};
    --bg-color: {theme.bgColor};
    --theme-bg-color: {theme.bgColor};
    --theme-bg-color-dark: {theme.bgColorDark};
    --theme-border-color: {theme.borderColor};
    --theme-border-color-dark: {theme.borderColorDark};
    --btn-primary-color: {theme.primaryColor};
    --btn-bg-color: {theme.primaryColor};
    --font-family: {finalFontFamily}; 
    --theme-font-family: {finalFontFamily};
    --transition: {theme.transition};
    --radius: {theme.radius};
    --disabled-opacity: {theme.disabledOpacity};
  "
>
  <slot></slot>

  {#if showToggle}
    <PreviewBarsToggle visible={barsVisible} on:toggle={handleToggle} />
  {/if}

  {#if showPreviewComponents}
    <!-- These navigators will be persistent across main survey routes, but not stats routes -->
    {#if survey}
      <QuestionNavigator {survey} {currentIndex} forceVisible={showPreviewComponents} hidden={!barsVisible} />
    {/if}
    <ProfileNavigator forceVisible={showPreviewComponents} hidden={!barsVisible} />
  {/if}
</div>

<style>
  .shared-layout {
    width: 100%;
    position: relative;
  }
</style> 